import { openPopup } from '@neb/popup';

import { getEnterpriseAdmin } from '../../../../../neb-api-client/src/enterprise-tenant-permissions-api-client';
import { getPracticeUser } from '../../../../../neb-api-client/src/permissions-api-client';
import { setItem } from '../../../../../neb-login/neb-session-state';
import { POPUP_RENDER_KEYS } from '../../../../../neb-popup/src/renderer-keys';
import { store } from '../../../../../neb-redux/neb-redux-store';
import {
  hasFeatureOrBeta,
  FEATURE_FLAGS,
} from '../../../../../neb-utils/feature-util';

/**
 * Helper function to get commonly used session state values
 * @returns {Object} Session state values
 */
export const getSessionState = () => {
  const state = store.getState();
  const sessionItem = state?.session?.item || {};

  return {
    currentUserId: sessionItem.id,
    userId: sessionItem.id,
    sessionEnterpriseId: sessionItem.enterpriseId,
    sessionEnterpriseAdmin: sessionItem.enterpriseAdmin,
    currentTenantId: sessionItem.tenantId,
    cognitoType: sessionItem.type,
    currentSession: sessionItem,
  };
};

/**
 * Helper function to update session with new enterprise data
 * @param {string|null} enterpriseId - The enterprise ID to set
 * @param {boolean|null} enterpriseAdmin - The enterprise admin status to set
 */
const updateSessionEnterpriseData = (enterpriseId, enterpriseAdmin) => {
  const state = store.getState();
  const currentSession = state?.session?.item || {};

  const updatedSession = {
    ...currentSession,
    enterpriseId,
    enterpriseAdmin,
  };

  store.dispatch(setItem(updatedSession));
};

/**
 * Checks if the security tab should be displayed for a user form
 * @param {Object} model - The form model
 * @param {boolean} hasFeatureFlag - Whether the 2FA feature flag is enabled
 * @returns {boolean}
 */
export const shouldDisplaySecurityTab = (model, hasFeatureFlag) => {
  if (!hasFeatureFlag) {
    return false;
  }

  const { currentUserId } = getSessionState();
  return model && model.id === currentUserId;
};

/**
 * Checks if the enterprise reports tab should be displayed for a user form
 *
 * Business Logic:
 * - Support App: Always show the tab (no restrictions)
 * - Practice App: Only show if ALL conditions are met:
 *   1. ENTERPRISE_REPORTS feature flag is enabled
 *   2. Current logged-in user has enterpriseId (is part of an enterprise)
 *   3. Current logged-in user has enterpriseAdmin=true (has admin privileges)
 *
 * This ensures that in the practice app, only enterprise admins can see and manage
 * enterprise permissions. When users disable their own enterprise admin status,
 * they keep their enterpriseId but lose enterpriseAdmin, hiding the tab.
 *
 * @param {Object} model - The form model containing user data (not used for permission check)
 * @param {boolean} hasFeatureFlag - Whether the ENTERPRISE_REPORTS feature flag is enabled
 * @param {boolean} isSupportApp - Whether we're in the support app context
 * @returns {Promise<boolean>} True if the Enterprise Permissions tab should be displayed, false otherwise
 */
const fetchCurrentUserEnterpriseAdmin = async (
  currentTenantId,
  currentUserId,
) => {
  try {
    const enterpriseAdminResponse = await getEnterpriseAdmin(
      currentTenantId,
      currentUserId,
    );

    const rawEnterpriseAdmin = enterpriseAdminResponse?.enterpriseAdmin;
    const result = rawEnterpriseAdmin === 1 || rawEnterpriseAdmin === true;

    return result;
  } catch (error) {
    return false;
  }
};

const fetchCurrentUserEnterpriseId = async (
  currentUserId,
  existingEnterpriseId,
) => {
  if (existingEnterpriseId !== undefined) {
    return existingEnterpriseId;
  }

  try {
    const currentUserData = await getPracticeUser(currentUserId, true);
    return currentUserData?.enterpriseId;
  } catch (error) {
    return null;
  }
};

export const shouldDisplayEnterpriseReportsTab = async (
  model,
  hasFeatureFlag,
  isSupportApp = false,
) => {
  if (isSupportApp) {
    return true;
  }

  if (!hasFeatureFlag) {
    return false;
  }

  try {
    const { currentUserId, currentTenantId, sessionEnterpriseId } =
      getSessionState();

    const currentUserEnterpriseAdmin = await fetchCurrentUserEnterpriseAdmin(
      currentTenantId,
      currentUserId,
    );
    const currentUserEnterpriseId = await fetchCurrentUserEnterpriseId(
      currentUserId,
      sessionEnterpriseId,
    );

    updateSessionEnterpriseData(
      currentUserEnterpriseId,
      currentUserEnterpriseAdmin,
    );

    const hasEnterpriseId = !!currentUserEnterpriseId;
    const hasEnterpriseAdmin = currentUserEnterpriseAdmin === true;

    return hasEnterpriseId && hasEnterpriseAdmin;
  } catch (error) {
    return false;
  }
};

/**
 * Gets the security tab configuration
 * @param {Function} renderSecurityFn - The function to render the security tab content
 * @returns {Object} The security tab configuration
 */
export const getSecurityTabConfig = renderSecurityFn => ({
  id: 'security',
  label: 'Security',
  renderer: renderSecurityFn,
  fields: [
    'twoStepVerificationEnabled',
    'twoStepMethod',
    'twoStepPhoneNumber',
    'showTwoStepSetup',
  ],
});

/**
 * Initializes the 2FA feature flag
 * @returns {Promise<boolean>} Whether the 2FA feature flag is enabled
 */
export const initializeOwl2faFeatureFlag = () =>
  hasFeatureOrBeta(FEATURE_FLAGS.OWL_2FA_ENABLEMENT);

/**
 * Initializes the Enterprise Reports feature flag
 * @returns {Promise<boolean>} Whether the ENTERPRISE_REPORTS feature flag is enabled
 */
export const initializeEnterpriseReportsFeatureFlag = () =>
  hasFeatureOrBeta(FEATURE_FLAGS.ENTERPRISE_REPORTS);

/**
 * Checks if the current user is disabling their own enterprise admin
 * @param {Object} originalModel - The original model before changes (for user ID and current admin status)
 * @param {Object} updatedModel - The updated model with changes
 * @returns {boolean} True if current user is disabling their own enterprise admin
 */
export const isCurrentUserDisablingTheirOwnEnterpriseAdmin = (
  originalModel,
  updatedModel,
) => {
  const { currentUserId } = getSessionState();

  if (originalModel?.id !== currentUserId) {
    return false;
  }

  const originalEnterpriseAdmin = !!originalModel?.enterpriseAdmin;
  const isExplicitlyDisabled = updatedModel?.enterpriseAdmin === false;

  return originalEnterpriseAdmin && isExplicitlyDisabled;
};

/**
 * Shows confirmation popup for disabling enterprise admin permissions
 * @returns {Promise<boolean>} True if user confirmed, false if cancelled
 */
export const showEnterpriseAdminDisableConfirmation = async () => {
  try {
    const result = await openPopup(POPUP_RENDER_KEYS.CONFIRM, {
      title: 'Disable Enterprise Permissions',
      message:
        'Are you sure you want to disable your enterprise admin permissions? You will no longer be able to manage enterprise permissions for yourself or other users.',
      confirmText: 'Yes, Disable',
      cancelText: 'Cancel',
    });

    return result;
  } catch (error) {
    return false;
  }
};

/**
 * Reverts enterprise admin changes and marks form as clean
 * @param {Object} formService - The form service instance
 * @param {Object} originalModel - The original model to revert to
 * @param {Function} onChangeDirty - Function to mark form as clean/dirty
 */
export const revertEnterpriseAdminChanges = (
  formService,
  originalModel,
  onChangeDirty,
) => {
  formService.apply('enterpriseAdmin', originalModel.enterpriseAdmin);
  onChangeDirty(false);
};

/**
 * Handles when the current user disables their own enterprise admin privileges
 * Updates the session to ensure immediate UI changes
 * NOTE: User keeps their enterpriseId (stays in enterprise) but loses enterpriseAdmin privileges
 */
export const handleCurrentUserEnterpriseAdminDisabled = () => {
  const { sessionEnterpriseId } = getSessionState();
  updateSessionEnterpriseData(sessionEnterpriseId, false);
};

/**
 * Common save handler logic for enterprise admin confirmation
 * Handles the popup confirmation flow and post-save actions
 * @param {Object} params - Parameters object
 * @param {Object} params.originalModel - The original model before changes
 * @param {Object} params.updatedModel - The updated model with changes
 * @param {Object} params.formService - The form service instance
 * @param {Function} params.onChangeDirty - Function to mark form as clean/dirty
 * @returns {Promise<boolean>} True if should proceed with save, false if cancelled
 */
export const handleEnterpriseAdminSaveConfirmation = async ({
  originalModel,
  updatedModel,
  formService,
  onChangeDirty,
}) => {
  const isCurrentUserDisablingThemselves =
    isCurrentUserDisablingTheirOwnEnterpriseAdmin(originalModel, updatedModel);

  if (isCurrentUserDisablingThemselves) {
    const confirmed = await showEnterpriseAdminDisableConfirmation();

    if (!confirmed) {
      revertEnterpriseAdminChanges(formService, originalModel, onChangeDirty);
      return false;
    }
  }

  return true;
};

/**
 * Common logic for adding Enterprise Permissions tab in connectedCallback
 * @param {Object} params - Parameters object
 * @param {Array} params.navItems - Current navigation items array
 * @param {Function} params.renderEnterpriseAdmin - Function to render enterprise admin tab
 * @param {boolean} params.isSupportApp - Whether we're in support app
 * @param {Object} params.model - The form model
 * @param {boolean} params.hasEnterpriseReportsFF - Whether enterprise reports feature flag is enabled
 * @returns {Promise<Array>} Updated navigation items array
 */
export const addEnterprisePermissionsTab = async ({
  navItems,
  renderEnterpriseAdmin,
  model,
  isSupportApp = false,
  hasEnterpriseReportsFF = false,
}) => {
  const shouldDisplay = await shouldDisplayEnterpriseReportsTab(
    model,
    hasEnterpriseReportsFF,
    isSupportApp,
  );

  if (shouldDisplay) {
    const updatedNavItems = [
      ...navItems,
      {
        id: 'enterpriseAdmin',
        label: 'Enterprise Permissions',
        renderer: renderEnterpriseAdmin,
        fields: ['enterpriseAdmin', 'enterpriseTenantPermissions'],
      },
    ];
    return updatedNavItems;
  }

  return navItems;
};
